import { cn } from '@/utils/cn'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef } from 'react'

export type RadioProps = ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>

export const RadioGroup = forwardRef<ElementRef<typeof RadioGroupPrimitive.Root>, RadioProps>(
  ({ className, ...props }, ref) => {
    return (
      <RadioGroupPrimitive.Root
        className={cn(
          'flex items-center',
          'h-9 gap-1 rounded-full border p-1 text-sm',
          'bg-input transition-all',
          'has-[:focus-visible]:border-focus has-[:focus-visible]:ring-2',
          className
        )}
        {...props}
        ref={ref}
      />
    )
  }
)
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName

export const RadioTile = forwardRef<
  ElementRef<typeof RadioGroupPrimitive.Item>,
  ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        'flex flex-grow items-center justify-center',
        'h-full rounded-full px-2',
        'whitespace-nowrap text-body-foreground leading-none outline-none',
        'transition-all',
        'hocus:bg-input-hover hocus:text-active-foreground',
        'data-[state=checked]:bg-accent data-[state=checked]:text-button-foreground',
        'data-[state=checked]:hocus:bg-accent-hover data-[state=checked]:hocus:text-button-foreground',
        '',
        className
      )}
      {...props}
    />
  )
})

export const RadioButtonGroup = forwardRef<
  ElementRef<typeof RadioGroupPrimitive.Root>,
  ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ ...props }, ref) => <RadioGroupPrimitive.Root {...props} ref={ref} />)
RadioButtonGroup.displayName = RadioGroupPrimitive.Root.displayName

export const RadioButton = forwardRef<
  ElementRef<typeof RadioGroupPrimitive.Item>,
  ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    className={cn(
      'peer flex size-5 shrink-0 items-center justify-center rounded-full border border-border-strong bg-input outline-none transition-colors',
      'hocus:bg-input-hover hover:border-body-foreground',
      'focus-visible:border-focus focus-visible:ring-2',
      'data-[state=checked]:border-accent data-[state=checked]:bg-accent',
      'data-[state=checked]:[&:not(:disabled)]:hocus:border-accent-hover data-[state=checked]:[&:not(:disabled)]:hocus:bg-accent-hover',
      'disabled:cursor-not-allowed disabled:bg-input-disabled disabled:opacity-100',
      className
    )}
    {...props}
  >
    <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
      <div className="size-2 rounded-full bg-button-foreground" />
    </RadioGroupPrimitive.Indicator>
  </RadioGroupPrimitive.Item>
))
RadioButton.displayName = RadioGroupPrimitive.Item.displayName
